/* global Blockly */
/* global $ */
/* global Util */
import React from 'react';
import { plusImage, minusImage, IMAGE_WIDTH, IMAGE_HEIGHT, getDropdownFields, addDropdownKey, removeDropdownKey, updateDropdownsAfterChange,
    openSetSchemaWindow, generateOutputPathCode } from '../../helpers/jsonWithSchema';
import { resolveBlockSchema, storeBlockSchema } from '../../helpers/blocklySchemaStorage';
import { _defineHelpUrlandToolTip } from '../../../v1/helpers/index.js'

const treatNullSchemaAsUnknownType = true; // Sanitized null values represent an unknown Blockly output type.

function defineBlock() {
    return {
        init: function () {
            this.jsonStructure = this.jsonStructure || {};
            this.hasSchema = this.hasSchema === true; // Normalize the initial state; domToMutation sets this to true when a saved schema reference is resolved.
            this.appendValueInput('JSON_OBJECT')
                .appendField('from JSON object')
                .setCheck(['String', 'Object', 'Array']);
            this.dropdownInput = this.appendDummyInput('DROPDOWN_PATH')
                                     .appendField('get', 'LABEL')
                                     .appendField(new Blockly.FieldImage(plusImage, IMAGE_WIDTH, IMAGE_HEIGHT, '*', () => this.addKey()), 'PLUS')
                                     .appendField(new Blockly.FieldImage(minusImage, IMAGE_WIDTH, IMAGE_HEIGHT, '*', () => this.deleteKey()), 'DELETE');
            this.setColour(Blockly.Msg.LISTS_HUE);
            this.setOutput(true);
            this.setOutputShape(Blockly.OUTPUT_SHAPE_ROUND);
            this.setTooltip('Get a value nested within a JSON object. If the input is a string, the string will be parsed into a JSON object.');
            this.setHelpUrl(Blockly.BASE_HELP_URL + "#lists");
            // initialize Set Schema button
            this.appendDummyInput('SCHEMA')
                .appendField(new Blockly.FieldButton('Set Schema', this.setSchema, () => this.setSchemaButtonHandler()), 'SCHEMA');
            this.updateSchemaButtonLabel();
        },

        mutationToDom: function () {
            // get the selected path from the dropdown fields
            const selectedPath = getDropdownFields(this.dropdownInput).map(field => field.getValue());

            // mutation keys are strictly lowercase in Blockly
            const mutationAttributes = { selectedpath: selectedPath };
            if (this.hasSchema && this.id) {
                mutationAttributes.schemarefid = this.id;
            }
            const mutation = Util.dom('mutation', mutationAttributes);

            return mutation;
        },

        domToMutation: function (xml) {
            // get the selected path for the dropdown fields as an array
            let selectedPath = xml.getAttribute('selectedpath');
            selectedPath = selectedPath ? selectedPath.split(',') : [];

            // resolve the design-time schema without storing it in Blockly XML
            const storedReference = xml.getAttribute('schemarefid');
            const resolvedSchema = resolveBlockSchema(this.workspace, storedReference);
            this.hasSchema = resolvedSchema.found;
            this.jsonStructure = resolvedSchema.found ? resolvedSchema.schema : {};
            // If Blockly created a copy with a new block ID, store the loaded schema under that new ID.
            if (resolvedSchema.found && this.id && storedReference !== this.id) {
                this.jsonStructure = storeBlockSchema(this.workspace, this.id, this.jsonStructure);
            }
            this.updateSchemaButtonLabel();

            if (this.hasSchema && selectedPath.length) {
                this.buildDropdownTree(selectedPath);
            }
        },

        updateSchemaButtonLabel: function () {
            const schemaButton = this.getField?.('SCHEMA');
            if (!schemaButton) return;

            const label = this.hasSchema ? 'Update Schema' : 'Set Schema';
            schemaButton.buttontext_ = label;
            schemaButton.setText(label);
        },

        buildDropdownTree: function (selectedPath) {
            for (const selectedValue of selectedPath) {
                this.addKey(selectedValue);
            }
        },

        addKey: function (selectedValue = null) {
            addDropdownKey({
                input: this.dropdownInput,
                jsonStructure: this.jsonStructure,
                selectedValue: selectedValue,
                updateCallback: this.update.bind(this),
                plusClick: () => this.addKey(),
                minusClick: () => this.deleteKey(),
                treatNullSchemaAsUnknownType
            });
        },

        deleteKey: function () {
            removeDropdownKey({
                input: this.dropdownInput,
                jsonStructure: this.jsonStructure,
                treatNullSchemaAsUnknownType
            });
        },

        update: function (level) {
            updateDropdownsAfterChange({
                input: this.dropdownInput,
                jsonStructure: this.jsonStructure,
                changedDropdownIndex: level,
                treatNullSchemaAsUnknownType
            });
        },

        setSchemaButtonHandler: function () {
            openSetSchemaWindow({
                blockRef: this,
                includeQueryButton: false,
                includeCopyButton: false,
                inputSizeLimit: 1 * 1024 * 1024, // 1 MB limit
                initialSchema: this.hasSchema ? this.jsonStructure : undefined,
                applyHandler: (responseData) => {
                    this.jsonStructure = storeBlockSchema(this.workspace, this.id, responseData);
                    this.hasSchema = true;
                    this.updateSchemaButtonLabel();
                    this.update(-1); // remove all existing dropdowns since the JSON structure has now changed
                }
            });
        }
    };
}

function defineGenerators() {
    return {
        'JavaScript': function (block) {
            const dropdownFields = getDropdownFields(block.dropdownInput);
            let jsonObject = Blockly.JavaScript.valueToCode(block, 'JSON_OBJECT', Blockly.JavaScript.ORDER_NONE);

            // parse input from string to JSON if input is a string
            // if input is a string that is not valid JSON, return an empty string
            jsonObject = jsonObject ? `
                (function () {
                    if (typeof ${jsonObject} !== 'string') return ${jsonObject};
                    try { return JSON.parse(${jsonObject}); } catch (e) { return ''; }
                })()` : '';

            return generateOutputPathCode({
                baseExpression: `(${jsonObject})`,
                dropdownFields: dropdownFields,
                includeStructureComment: false
            });
        }
    };
}

const GetFromJSONWithSchema = function () {
    const name = 'get_from_json_with_schema_V3';
    GetFromJSONWithSchema.blockName = name;

    Blockly.Blocks[name] = defineBlock();
    const generators = defineGenerators();
    for (let i in generators) {
        Blockly[i][name] = generators[i];
    }

    _defineHelpUrlandToolTip(name,  Blockly.BASE_HELP_URL + "#lists");

    return <block type={name}></block>;
}

export default GetFromJSONWithSchema;
